import type { FormEvent, ReactNode } from 'react';

import AdminCatalogImageField from './AdminCatalogImageField';
import LucideSymbolIcon from '../../icons/LucideSymbolIcon';

export type DomaineFormValues = {
  code: string;
  libelle: string;
  slug: string;
  intro: string;
  urlImage: string;
  icon: string;
  ordre: string;
  estActive: boolean;
};

type Props = {
  mode: 'create' | 'edit';
  values: DomaineFormValues;
  onChange: (patch: Partial<DomaineFormValues>) => void;
  onSubmit: (event: FormEvent) => void;
  onCancel?: () => void;
  isSaving?: boolean;
  onUploadError?: (message: string) => void;
};

const fieldClass =
  'academia-field w-full rounded-xl border border-outline-variant/25 bg-white px-3.5 py-2.5 text-sm text-slate-900 shadow-sm transition focus:border-primary/40 focus:ring-2 focus:ring-primary/15';

function FieldLabel({
  children,
  hint,
  required,
}: {
  children: ReactNode;
  hint?: string;
  required?: boolean;
}) {
  return (
    <span className="mb-1.5 block">
      <span className="text-xs font-bold uppercase tracking-wide text-slate-700">
        {children}
        {required ? <span className="text-primary"> *</span> : null}
      </span>
      {hint ? <span className="mt-0.5 block text-[11px] font-normal normal-case text-on-surface-variant">{hint}</span> : null}
    </span>
  );
}

function SectionHeading({ icon, title, description }: { icon: string; title: string; description: string }) {
  return (
    <div className="flex gap-3">
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
        <LucideSymbolIcon name={icon} tone="primary" size={20} />
      </span>
      <div className="min-w-0">
        <h4 className="text-sm font-black text-slate-900">{title}</h4>
        <p className="mt-0.5 text-xs leading-relaxed text-on-surface-variant">{description}</p>
      </div>
    </div>
  );
}

export default function DomaineFormationForm({
  mode,
  values,
  onChange,
  onSubmit,
  onCancel,
  isSaving = false,
  onUploadError,
}: Props) {
  const isCreate = mode === 'create';

  return (
    <form
      onSubmit={onSubmit}
      className="min-w-0 overflow-hidden rounded-2xl border border-outline-variant/20 bg-white shadow-sm ring-1 ring-slate-900/[0.03]"
    >
      <div className="border-b border-outline-variant/15 bg-gradient-to-r from-primary/[0.06] via-white to-white px-5 py-4 sm:px-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-primary">
              {isCreate ? 'Nouveau domaine' : 'Modification'}
            </p>
            <h3 className="mt-1 text-base font-black text-slate-900">
              {isCreate ? 'Créer une famille de filières' : values.libelle || 'Domaine de formation'}
            </h3>
          </div>
          {values.icon ? (
            <span className="flex h-11 w-11 items-center justify-center rounded-2xl border border-outline-variant/15 bg-white shadow-sm">
              <LucideSymbolIcon name={values.icon} tone="primary" size={22} />
            </span>
          ) : null}
        </div>
      </div>

      <div className="grid min-w-0 gap-0 lg:grid-cols-5">
        <div className="min-w-0 space-y-5 border-b border-outline-variant/10 p-5 sm:p-6 lg:col-span-2 lg:border-b-0 lg:border-r">
          <SectionHeading
            icon="badge"
            title="Identité & référentiel"
            description="Code technique, libellé affiché et paramètres de classement."
          />

          {isCreate ? (
            <label className="block min-w-0">
              <FieldLabel required hint="Identifiant unique en majuscules (ex. NUMERIQUE).">
                Code domaine
              </FieldLabel>
              <input
                required
                className={`${fieldClass} font-mono uppercase`}
                value={values.code}
                onChange={(e) => onChange({ code: e.target.value.toUpperCase() })}
                placeholder="NUMERIQUE"
              />
            </label>
          ) : null}

          <label className="block min-w-0">
            <FieldLabel required>Libellé public</FieldLabel>
            <input
              required
              className={fieldClass}
              value={values.libelle}
              onChange={(e) => onChange({ libelle: e.target.value })}
              placeholder="Filières numériques et technologiques"
            />
          </label>

          <label className="block min-w-0">
            <FieldLabel hint="Segment d’URL sur le site vitrine.">Slug URL</FieldLabel>
            <div className="flex min-w-0 items-center gap-2">
              <span className="hidden shrink-0 rounded-lg bg-surface-container-high px-2.5 py-2 text-xs font-mono text-on-surface-variant sm:inline">
                /filieres/
              </span>
              <input
                className={`${fieldClass} min-w-0 font-mono`}
                value={values.slug}
                onChange={(e) => onChange({ slug: e.target.value.toLowerCase() })}
                placeholder="numeriques-technologiques"
              />
            </div>
          </label>

          <div className="grid grid-cols-2 gap-3">
            <label className="block min-w-0">
              <FieldLabel hint="Icône Lucide.">Icône</FieldLabel>
              <input
                className={fieldClass}
                value={values.icon}
                onChange={(e) => onChange({ icon: e.target.value })}
                placeholder="monitoring"
              />
            </label>
            <label className="block min-w-0">
              <FieldLabel hint="Ordre d’affichage.">Ordre</FieldLabel>
              <input
                type="number"
                className={fieldClass}
                value={values.ordre}
                onChange={(e) => onChange({ ordre: e.target.value })}
              />
            </label>
          </div>

          {!isCreate ? (
            <label className="flex cursor-pointer items-center justify-between gap-3 rounded-xl border border-outline-variant/20 bg-surface-container-low/60 px-4 py-3">
              <div>
                <span className="text-sm font-semibold text-slate-900">Domaine actif</span>
                <p className="text-xs text-on-surface-variant">Visible dans le catalogue public.</p>
              </div>
              <input
                type="checkbox"
                className="h-5 w-5 rounded border-outline-variant/40 text-primary focus:ring-primary/30"
                checked={values.estActive}
                onChange={(e) => onChange({ estActive: e.target.checked })}
              />
            </label>
          ) : null}
        </div>

        <div className="min-w-0 space-y-5 bg-gradient-to-br from-slate-50/90 via-white to-primary/[0.03] p-5 sm:p-6 lg:col-span-3">
          <SectionHeading
            icon="public"
            title="Vitrine web & mobile"
            description="Texte et visuel présentés aux apprenants sur le catalogue."
          />

          <label className="block min-w-0">
            <FieldLabel hint="Affiché sous le titre du domaine sur la page publique, et sur le carrousel d’accueil.">
              Texte descriptif
            </FieldLabel>
            <textarea
              className={`${fieldClass} min-h-[110px] resize-y leading-relaxed`}
              value={values.intro}
              onChange={(e) => onChange({ intro: e.target.value })}
              rows={4}
              placeholder="Décrivez le domaine : public visé, types de compétences, débouchés…"
            />
          </label>

          <AdminCatalogImageField
            value={values.urlImage}
            onChange={(urlImage) => onChange({ urlImage })}
            inputClass={fieldClass}
            onUploadError={onUploadError}
          />
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-end gap-2 border-t border-outline-variant/15 bg-surface-container-low/40 px-5 py-4 sm:px-6">
        {onCancel ? (
          <button
            type="button"
            onClick={onCancel}
            className="rounded-xl border border-outline-variant/30 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50"
          >
            Annuler
          </button>
        ) : null}
        <button
          type="submit"
          disabled={isSaving}
          className="inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-2.5 text-sm font-bold text-white shadow-md shadow-primary/20 hover:opacity-95 disabled:opacity-50"
        >
          <LucideSymbolIcon name={isCreate ? 'add' : 'save'} tone="inherit" size={18} className="text-white" />
          {isSaving ? 'Enregistrement…' : isCreate ? 'Créer le domaine' : 'Enregistrer'}
        </button>
      </div>
    </form>
  );
}
